// A large circle + value label rendered on top of the regular icons to indicate
// the currently selected site and show its latest reading. Implemented as an HTML
// Marker (not a canvas layer) so it sits above multi-gauge HTML markers via z-index.

import { Marker } from "maplibre-gl";
import type { Map } from "maplibre-gl";

import type { GaugeFeature } from "../api.ts";

import type { DisplayType } from "../../../shared/types.ts";

import { ALERT_COLORS } from "./map-colors.ts";

function decimalPlaces(displayType: DisplayType): number {
  if (displayType === "well") return 2;
  if (displayType === "river") return 0;
  return 1; // rainfall
}

let _selectedMarker: Marker | null = null;

// onToggle: if provided (multi-gauge site), clicking the circle switches to the twin gauge.
export function setSelectedGauge(
  map: Map,
  feature: GaugeFeature | null,
  onToggle?: () => void,
): void {
  if (_selectedMarker) {
    _selectedMarker.remove();
    _selectedMarker = null;
  }
  if (!feature) return;

  const alertLevel = feature.properties.alert_level;
  const bgColor = (alertLevel === "high" ? ALERT_COLORS.high
    : alertLevel === "watch" ? ALERT_COLORS.watch
    : ALERT_COLORS.normal)!;

  const raw = Number(feature.properties.value);
  const value = isNaN(raw) ? "—" : raw.toFixed(decimalPlaces(feature.properties.display_type));

  const el = document.createElement("div");
  el.className = "selected-marker" + (onToggle ? " toggleable" : "");
  el.style.background = bgColor;
  el.innerHTML = `<span>${value}</span><span>${feature.properties.unit ?? ""}</span>`;

  if (onToggle) {
    el.addEventListener("click", (e) => { e.stopPropagation(); onToggle(); });
  }

  const [lng, lat] = feature.geometry.coordinates as [number, number];
  _selectedMarker = new Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
}
